/**
 * Liquid Cooling CDU Configuration Types (Frontend)
 * TypeScript interfaces and Zod schemas for the repeating Liquid Cooling CDU section (#996 Phase 3)
 *
 * IMPORTANT: Zod schemas must be wrapped in useMemo() for iOS to avoid crashes
 * @see GitLab #996 — Repeating Sections (Phase 3: Liquid Cooling)
 *
 * Contract note: status/name use the GENERIC repeating-section column names
 * `status`/`name` (NOT `liquidCoolingCduStatus`/`liquidCoolingCduName`), mirroring the
 * Small UPS / Cooling Phase 2 types.
 *
 * SHARED liquid cooling fields (lcLiquidToChipDesired, lcAdditionalInformationSfn) and all
 * other legacy SFN/TCS/datahall lc_* fields are NOT part of this per-instance model — they
 * stay on the parent request and are rendered once at step level (#996 Phase 3).
 */

import { z } from 'zod';

export type LiquidCoolingCduStatus = 'existing' | 'new';

/**
 * Liquid Cooling CDU Configuration interface (camelCase — matches API response)
 */
export interface LiquidCoolingCduConfiguration {
  id?: number;
  requestId: number;

  // Existing vs New status — generic repeating-section field (#996)
  status: LiquidCoolingCduStatus;

  // User-defined instance name (auto-named "CDU N" server-side when omitted)
  name?: string;

  locationTagId?: number | null;

  // Per-instance Liquid Cooling CDU fields (6) — keys mirror the live UI dataField keys
  lcCduType?: string;
  lcVertivSuppliesRowManifolds?: boolean;
  lcVertivSuppliesRackManifolds?: boolean;
  lcCduRedundancy?: string;
  lcLeakDetectionApproach?: string;
  lcDescription?: string;

  // #997: per-CDU "Additional Information" note (per-instance value shown per CDU).
  lcAdditionalInformationSfn?: string;

  // Metadata
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Input for creating a new liquid cooling CDU configuration
 */
export type CreateLiquidCoolingCduConfigurationInput = Omit<
  LiquidCoolingCduConfiguration,
  'id' | 'createdAt' | 'updatedAt'
>;

/**
 * Input for updating a liquid cooling CDU configuration
 */
export type UpdateLiquidCoolingCduConfigurationInput = Partial<
  Omit<
    LiquidCoolingCduConfiguration,
    'id' | 'requestId' | 'createdAt' | 'updatedAt'
  >
>;

/**
 * Grouped liquid cooling CDU configurations for the segmented view (existing vs new)
 */
export interface GroupedLiquidCoolingCduConfigurations {
  existing: LiquidCoolingCduConfiguration[];
  new: LiquidCoolingCduConfiguration[];
}

/**
 * API response for liquid cooling CDU configurations list
 */
export interface LiquidCoolingCduConfigurationsResponse {
  liquidCoolingCdus: LiquidCoolingCduConfiguration[];
  total: number;
}

/**
 * Zod schema factory for liquid cooling CDU configuration validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 *
 * @example
 * const schema = useMemo(() => createLiquidCoolingCduConfigurationSchema(), []);
 */
export const createLiquidCoolingCduConfigurationSchema = () =>
  z.object({
    id: z.number().optional(),
    requestId: z.number(),
    status: z.enum(['existing', 'new']),

    // User-defined instance name
    name: z.string().optional(),

    locationTagId: z.number().int().nullable().optional(),

    // Per-instance fields
    lcCduType: z.string().optional(),
    lcVertivSuppliesRowManifolds: z.boolean().optional(),
    lcVertivSuppliesRackManifolds: z.boolean().optional(),
    lcCduRedundancy: z.string().optional(),
    lcLeakDetectionApproach: z.string().optional(),
    lcDescription: z.string().optional(),
    lcAdditionalInformationSfn: z.string().optional(),

    // Metadata
    displayOrder: z.number().min(0),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  });

/**
 * Zod schema factory for create liquid cooling CDU input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const createLiquidCoolingCduInputSchema = () =>
  createLiquidCoolingCduConfigurationSchema().omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  });

/**
 * Zod schema factory for update liquid cooling CDU input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const updateLiquidCoolingCduInputSchema = () =>
  createLiquidCoolingCduConfigurationSchema()
    .omit({
      id: true,
      requestId: true,
      createdAt: true,
      updatedAt: true,
    })
    .partial();

/**
 * Type inference from Zod schemas
 */
export type LiquidCoolingCduConfigurationSchema = z.infer<
  ReturnType<typeof createLiquidCoolingCduConfigurationSchema>
>;
export type CreateLiquidCoolingCduInputSchema = z.infer<
  ReturnType<typeof createLiquidCoolingCduInputSchema>
>;
export type UpdateLiquidCoolingCduInputSchema = z.infer<
  ReturnType<typeof updateLiquidCoolingCduInputSchema>
>;

/** Status options for liquid cooling CDU instances. */
export const LIQUID_COOLING_CDU_STATUS_OPTIONS: LiquidCoolingCduStatus[] = [
  'existing',
  'new',
];

/**
 * Default values for a new liquid cooling CDU configuration
 */
export const DEFAULT_LIQUID_COOLING_CDU_CONFIGURATION: Partial<LiquidCoolingCduConfiguration> =
  {
    status: 'new',
    displayOrder: 0,
  };
